import { Box, Divider, Link, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { VERSION } from 'version';
import DynamicFieldGroups from 'components/DynamicFieldGroups';
import { clearPendingQuoteLine, usePendingQuoteLine } from 'hooks/pendingQuoteLine';
import { useFeatures } from 'hooks/useFeatures';
import { useInboundProductLinking } from 'hooks/useInboundProductLinking';
import { isRemovedQuoteLine, pendingQuoteLine, showSet } from 'utils/form';
import { Field, FieldSetFragment as FieldSet, FieldSetVariant, FormType, InputType } from 'generated/graphql';
import FormGroups, { TWO_COL_FORMS } from './FormGroups';
import PendingQuoteLine from './PendingQuoteLine';

const FormSets = ({
  hasWhiteFields = false,
  fieldSets,
  onFieldUpdateNew,
  isLoading = false,
  onRefresh,
  onFormRefresh,
  isFormDialog,
  autoFocusSet = -1,
  isQCStorage = false,
  formType,
  onClose,
}: {
  hasWhiteFields?: boolean;
  fieldSets?: FieldSet[];
  isLoading?: boolean;
  isFormDialog: boolean;
  onFieldUpdateNew: (
    fieldGroup: Field[],
    field: Field,
    value: string,
    values?: string[],
    entityID?: string,
  ) => Promise<void>;
  onRefresh?: () => void;
  onFormRefresh?: () => void;
  autoFocusSet?: number;
  isQCStorage?: boolean;
  formType: FormType;
  onClose?: () => void;
}) => {
  const fields = useMemo(
    () => fieldSets?.flatMap((set) => set.groups.flatMap((group) => group.fields)) || [],
    [fieldSets],
  );
  const { isEnabled } = useFeatures();
  const isShowingOnlyNeededDividers = isEnabled('QuotesSearch') && formType === FormType.SetQuote;
  const isHoldingRemovedLineSpace = isEnabled('QuotesSearch') && formType === FormType.SetQuote;
  const inboundLinking = useInboundProductLinking(formType, fields, isLoading);
  const pendingLine = usePendingQuoteLine();
  const isShowingPendingLine = isEnabled('QuotesSearch') && formType === FormType.SetQuote && !!pendingLine;
  const searchSetIndex = fieldSets?.findIndex((set) =>
    set.groups.some((group) => group.fields.some((field) => field.type === InputType.Search)),
  );
  const pendingSetIndex = searchSetIndex !== undefined && searchSetIndex >= 0 ? searchSetIndex + 1 : -1;
  // The refetch can answer from cache without reporting loading, so watch the line count.
  const listedLineCount = pendingSetIndex >= 0 ? fieldSets?.[pendingSetIndex]?.groups.length : undefined;
  const pendingLineTemplate =
    pendingSetIndex >= 0
      ? fieldSets?.[pendingSetIndex]?.groups.findLast((group) =>
          group.fields.some((field) => field.type === InputType.Numberstepper),
        )
      : undefined;
  const lineCountWhenPending = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!pendingLine) {
      lineCountWhenPending.current = undefined;
    } else if (lineCountWhenPending.current === undefined) {
      lineCountWhenPending.current = listedLineCount;
    } else if (listedLineCount !== lineCountWhenPending.current) {
      clearPendingQuoteLine();
    }
  }, [pendingLine, listedLineCount]);
  const removedLineCount = isHoldingRemovedLineSpace
    ? (fieldSets?.[pendingSetIndex]?.groups.filter((group) => isRemovedQuoteLine(group, formType, true)).length ?? 0)
    : 0;
  const [listedLinesElement, setListedLinesElement] = useState<HTMLElement | null>(null);
  const [removedLinesSpace, setRemovedLinesSpace] = useState(0);
  const spacerRef = useRef<HTMLDivElement | null>(null);
  const isExpectingShrink = useRef(false);
  const listedHeight = useRef<number | undefined>(undefined);
  const removedLinesBefore = useRef(removedLineCount);
  if (removedLineCount > removedLinesBefore.current) isExpectingShrink.current = true;
  removedLinesBefore.current = removedLineCount;
  useEffect(() => {
    if (!listedLinesElement) return;
    const observer = new ResizeObserver(([entry]) => {
      const height = entry.contentRect.height;
      const previous = listedHeight.current;
      if (isExpectingShrink.current && previous !== undefined && height < previous) {
        isExpectingShrink.current = false;
        setRemovedLinesSpace((space) => space + previous - height);
      }
      listedHeight.current = height;
    });
    observer.observe(listedLinesElement);
    return () => observer.disconnect();
  }, [listedLinesElement]);
  useEffect(() => {
    if (!removedLinesSpace) return;
    const collapseOutOfView = () => {
      if ((spacerRef.current?.getBoundingClientRect().top ?? 0) > window.innerHeight) setRemovedLinesSpace(0);
    };
    window.addEventListener('scroll', collapseOutOfView, { passive: true });
    return () => window.removeEventListener('scroll', collapseOutOfView);
  }, [removedLinesSpace]);

  if (!fieldSets) {
    return null;
  }
  return (
    <>
      {fieldSets.map((fieldSet, index) => {
        const firstGroup =
          index === autoFocusSet
            ? fieldSet.groups.findIndex((group) => group.fields.some((field) => field.type !== InputType.Hidden))
            : -1;

        const hasPendingLine = isShowingPendingLine && index === pendingSetIndex;
        if (!showSet(fieldSet.groups) && !hasPendingLine) return null;

        return (
          <Fragment key={index}>
            {fieldSet.title && (
              <Typography
                data-form-set-title
                sx={(theme) => ({
                  fontSize: '1rem',
                  mr: 1,
                  fontWeight: theme.typography.fontWeightMedium,
                  margin: theme.spacing(1, 0),
                })}
              >
                {fieldSet.title}
              </Typography>
            )}
            {fieldSet.subtitle && (
              <Typography
                variant="body1"
                sx={(theme) => ({
                  mt: theme.spacing(1),
                  color: theme.palette.text.secondary,
                })}
              >
                {fieldSet.subtitle}
              </Typography>
            )}
            {fieldSet.variant === FieldSetVariant.Dynamic ? (
              <DynamicFieldGroups
                key={index}
                fieldGroups={fieldSet.groups}
                onFieldUpdateNew={onFieldUpdateNew}
                isLoading={isLoading}
                formType={formType}
                formFields={fields}
                decorateField={inboundLinking?.decorateField}
                renderRowActions={inboundLinking?.renderRowActions}
                cardDividers={!!inboundLinking}
                fixedRows={formType === FormType.MoveOutboundShipmentInventory}
              />
            ) : (
              <>
                <Box
                  ref={index === pendingSetIndex ? setListedLinesElement : undefined}
                  sx={TWO_COL_FORMS.includes(formType) ? { mt: index === 0 ? 1 : 2, mb: 2 } : { marginY: 1 }}
                  key={index}
                >
                  <FormGroups
                    key={index}
                    hasWhiteFields={hasWhiteFields}
                    fieldGroups={fieldSet.groups}
                    isLoading={isLoading}
                    onFieldUpdateNew={onFieldUpdateNew}
                    onRefresh={onRefresh}
                    onFormRefresh={onFormRefresh}
                    isFormDialog={isFormDialog}
                    autoFocusGroup={firstGroup}
                    isQCStorage={isQCStorage}
                    formType={formType}
                    onClose={onClose}
                    node={
                      index === fieldSets.length - 1 &&
                      formType === FormType.ProfileQuickActions && (
                        <Stack
                          direction="row"
                          sx={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2,
                            marginTop: 2,
                          }}
                        >
                          {
                            <Link
                              target="_blank"
                              variant="body2"
                              href="https://www.notion.so/native/nileag/Terms-privacy-0d30952c2469465a83d9c50000bcb131?deepLinkOpenNewTab=true"
                            >
                              Terms & Conditions
                            </Link>
                          }
                          {<>•</>}
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: 'center',
                              color: 'text.secondary',
                            }}
                          >
                            Version: {VERSION}
                          </Typography>
                        </Stack>
                      )
                    }
                    formFields={fields}
                  />
                  {hasPendingLine && (
                    <PendingQuoteLine label={pendingLine}>
                      {pendingLineTemplate && (
                        <FormGroups
                          fieldGroups={[pendingQuoteLine(pendingLineTemplate, pendingLine)]}
                          hasWhiteFields={hasWhiteFields}
                          isLoading
                          onFieldUpdateNew={async () => {}}
                          isFormDialog={isFormDialog}
                          formType={formType}
                          formFields={fields}
                        />
                      )}
                    </PendingQuoteLine>
                  )}
                </Box>
                {(isShowingOnlyNeededDividers
                  ? fieldSets.slice(index + 1).some((set) => showSet(set.groups))
                  : index < fieldSets?.length - 1) && <Divider sx={{ borderWidth: 0.5 }} />}
              </>
            )}
          </Fragment>
        );
      })}
      {removedLinesSpace > 0 && <Box ref={spacerRef} sx={{ height: removedLinesSpace }} aria-hidden />}
    </>
  );
};

export default FormSets;
